import { supabaseDataProvider } from "ra-supabase-core";
import {
  withLifecycleCallbacks,
  type DataProvider,
  type GetListParams,
  type Identifier,
  type ResourceCallbacks,
} from "ra-core";
import type {
  ContactNote,
  Deal,
  DealNote,
  RAFile,
  Sale,
  SalesFormData,
  SignUpData,
} from "../../types";
import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import { ATTACHMENTS_BUCKET } from "../commons/attachments";
import { getIsInitialized } from "./authProvider";
import { getSupabaseClient } from "./supabase";

export type LoadWorkflowAction =
  | "approve_so"
  | "closed_lost"
  | "sos"
  | "approve_load"
  | "pca_hold"
  | "po_approved"
  | "bookings_confirmed"
  | "lg_hold"
  | "pickup_confirmed"
  | "ready_to_invoice"
  | "invoices_entered"
  | "remove_lg_hold"
  | "audit_complete"
  | "paid"
  | "remove_paid"
  | "resolve_sos"
  | "bulk_duplicate";

export type LoadWorkflowCheck = {
  result: boolean;
  warn?: boolean;
  detail: string;
};

export type LoadWorkflowResponse = {
  valid: boolean;
  authorized: boolean;
  executed?: boolean;
  newStatus?: string;
  paid?: boolean;
  checks: Record<string, LoadWorkflowCheck>;
  error?: string;
};

const getBaseDataProvider = () =>
  supabaseDataProvider({
    instanceUrl: import.meta.env.VITE_SUPABASE_URL,
    apiKey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
    supabaseClient: getSupabaseClient(),
    sortOrder: "asc,desc.nullslast" as any,
  });

const processCompanyLogo = async (params: any) => {
  const logo = params.data.logo;

  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
  }

  return {
    ...params,
    data: {
      ...params.data,
      logo,
    },
  };
};

const getDataProviderWithCustomMethods = () => {
  const baseDataProvider = getBaseDataProvider();

  return {
    ...baseDataProvider,
    async getList(resource: string, params: GetListParams) {
      if (resource === "companies") {
        return baseDataProvider.getList("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getList("contacts_summary", params);
      }
      if (resource === "activity_log") {
        const { data, total } = await baseDataProvider.getList(
          "activity_log",
          params,
        );
        // Rename snake_case view columns to camelCase to match Activity type
        return {
          data: data.map((row: any) => ({
            ...row,
            contactNote: row.contact_note ?? undefined,
            companyNote: row.company_note ?? undefined,
            dealNote: row.deal_note ?? undefined,
            loadNote: row.load_note ?? undefined,
            contact_note: undefined,
            company_note: undefined,
            deal_note: undefined,
            load_note: undefined,
          })),
          total,
        };
      }

      return baseDataProvider.getList(resource, params);
    },
    async getOne(resource: string, params: any) {
      if (resource === "companies") {
        return baseDataProvider.getOne("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getOne("contacts_summary", params);
      }

      return baseDataProvider.getOne(resource, params);
    },

    async signUp({ email, password, first_name, last_name }: SignUpData) {
      const response = await getSupabaseClient().auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name,
            last_name,
          },
        },
      });

      if (!response.data?.user || response.error) {
        console.error("signUp.error", response.error);
        throw new Error(response?.error?.message || "Failed to create account");
      }

      // Update the is initialized cache
      (getIsInitialized as any)._is_initialized_cache = true;

      return {
        id: response.data.user.id,
        email,
        password,
      };
    },
    async salesCreate(body: SalesFormData) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        data: Sale;
      }>("users", {
        method: "POST",
        body: {
          ...body,
          roles: body.roles ?? [],
        },
      });

      if (!data || error) {
        console.error("salesCreate.error", error);
        const errorDetails = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();
        throw new Error(errorDetails?.message || "Failed to create the user");
      }

      return data.data;
    },
    async salesUpdate(
      id: Identifier,
      data: Partial<Omit<SalesFormData, "password">>,
    ) {
      const {
        email,
        first_name,
        last_name,
        administrator,
        roles,
        avatar,
        disabled,
      } = data;

      const { data: updatedData, error } =
        await getSupabaseClient().functions.invoke<{
          data: Sale;
        }>("users", {
          method: "PATCH",
          body: {
            sales_id: id,
            email,
            first_name,
            last_name,
            administrator,
            roles: roles ?? [],
            disabled,
            avatar,
          },
        });

      if (!updatedData || error) {
        console.error("salesCreate.error", error);
        throw new Error("Failed to update account manager");
      }

      return updatedData.data;
    },
    async updatePassword(id: Identifier) {
      const { data: passwordUpdated, error } =
        await getSupabaseClient().functions.invoke<boolean>("update_password", {
          method: "PATCH",
          body: {
            sales_id: id,
          },
        });

      if (!passwordUpdated || error) {
        console.error("update_password.error", error);
        throw new Error("Failed to update password");
      }

      return passwordUpdated;
    },
    async unarchiveDeal(deal: Deal) {
      // get all deals where stage is the same as the deal to unarchive
      const { data: deals } = await baseDataProvider.getList<Deal>("deals", {
        filter: { stage: deal.stage },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "index", order: "ASC" },
      });

      // set index for each deal starting from 1, if the deal to unarchive is found, set its index to the last one
      const updatedDeals = deals.map((d, index) => ({
        ...d,
        index: d.id === deal.id ? 0 : index + 1,
        archived_at: d.id === deal.id ? null : d.archived_at,
      }));

      return await Promise.all(
        updatedDeals.map((updatedDeal) =>
          baseDataProvider.update("deals", {
            id: updatedDeal.id,
            data: updatedDeal,
            previousData: deals.find((d) => d.id === updatedDeal.id),
          }),
        ),
      );
    },
    async isInitialized() {
      return getIsInitialized();
    },
    async mergeContacts(sourceId: Identifier, targetId: Identifier) {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "merge_contacts",
        {
          method: "POST",
          body: { loserId: sourceId, winnerId: targetId },
        },
      );

      if (error) {
        console.error("merge_contacts.error", error);
        throw new Error("Failed to merge contacts");
      }

      return data;
    },
    async loadWorkflow(
      loadId: Identifier,
      action: LoadWorkflowAction,
      options?: { confirm?: boolean; reason?: string },
    ): Promise<LoadWorkflowResponse> {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "load_workflow",
        {
          method: "POST",
          body: {
            load_id: loadId,
            action,
            confirm: options?.confirm ?? false,
            reason: options?.reason,
          },
        },
      );

      if (error) {
        console.error("load_workflow.error", error);
        throw new Error("Failed to run load workflow");
      }

      return data as LoadWorkflowResponse;
    },
    async globalSearch(query: string) {
      if (!query || query.trim().length < 2) {
        return { contacts: [], companies: [], deals: [], loads: [] };
      }
      const q = query.trim();
      const [contacts, companies, deals, loads] = await Promise.all([
        baseDataProvider.getList("contacts_summary", {
          filter: {
            "@or": {
              "first_name@ilike": q,
              "last_name@ilike": q,
            },
          },
          pagination: { page: 1, perPage: 5 },
          sort: { field: "last_name", order: "ASC" },
        }),
        baseDataProvider.getList("companies_summary", {
          filter: { "name@ilike": q },
          pagination: { page: 1, perPage: 5 },
          sort: { field: "name", order: "ASC" },
        }),
        baseDataProvider.getList("deals", {
          filter: { "name@ilike": q },
          pagination: { page: 1, perPage: 5 },
          sort: { field: "name", order: "ASC" },
        }),
        baseDataProvider.getList("loads_summary", {
          filter: { "load_number@ilike": q },
          pagination: { page: 1, perPage: 5 },
          sort: { field: "load_number", order: "ASC" },
        }),
      ]);
      return {
        contacts: contacts.data,
        companies: companies.data,
        deals: deals.data,
        loads: loads.data,
      };
    },
    async getConfiguration(): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.getOne("configuration", {
        id: 1,
      });
      return (data?.config as ConfigurationContextValue) ?? {};
    },
    async updateConfiguration(
      config: ConfigurationContextValue,
    ): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.update("configuration", {
        id: 1,
        data: { config },
        previousData: { id: 1 },
      });
      return data.config as ConfigurationContextValue;
    },
  } satisfies DataProvider;
};

export type CrmDataProvider = ReturnType<
  typeof getDataProviderWithCustomMethods
>;

const processConfigLogo = async (logo: any): Promise<string> => {
  if (typeof logo === "string") return logo;
  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
    return logo.src;
  }
  return logo?.src ?? "";
};

const lifeCycleCallbacks: ResourceCallbacks[] = [
  {
    resource: "configuration",
    beforeUpdate: async (params) => {
      const config = params.data.config;
      if (config) {
        config.lightModeLogo = await processConfigLogo(config.lightModeLogo);
        config.darkModeLogo = await processConfigLogo(config.darkModeLogo);
      }
      return params;
    },
  },
  {
    resource: "contact_notes",
    beforeSave: async (data: ContactNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "company_notes",
    beforeSave: async (data: any, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi: any) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "load_notes",
    beforeSave: async (data: any, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi: any) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "deal_notes",
    beforeSave: async (data: DealNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "resale_certificates",
    beforeSave: async (data: any, _, __) => {
      if (data.attachment?.rawFile instanceof File) {
        data.attachment = await uploadToBucket(data.attachment);
      }
      return data;
    },
  },
  {
    resource: "sales",
    beforeGetList: async (params) => {
      return applyFullTextSearch(
        ["first_name", "last_name", "email"],
        {},
      )(params);
    },
    beforeSave: async (data: Sale, _, __) => {
      if (data.avatar) {
        await uploadToBucket(data.avatar);
      }
      return data;
    },
  },
  {
    resource: "contacts",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "first_name",
        "last_name",
        "company_name",
        "title",
        "email",
        "phone",
        "background",
      ])(params);
    },
  },
  {
    resource: "companies",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "name",
        "phone_number",
        "website",
        "zipcode",
        "city",
        "state_abbr",
      ])(params);
    },
    beforeCreate: async (params) => {
      const createParams = await processCompanyLogo(params);

      return {
        ...createParams,
        data: {
          created_at: new Date().toISOString(),
          ...createParams.data,
        },
      };
    },
    beforeUpdate: async (params) => {
      return await processCompanyLogo(params);
    },
  },
  {
    resource: "contacts_summary",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["first_name", "last_name"])(params);
    },
  },
  {
    resource: "deals",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "category", "description"])(params);
    },
  },
  {
    resource: "loads",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["load_number", "status"])(params);
    },
    beforeDelete: async (params, dataProvider) => {
      // Soft delete: set deleted = true instead of removing the row
      await dataProvider.update("loads", {
        id: params.id,
        data: { deleted: true },
        previousData: params.previousData,
      });
      // Return the previousData as the result to prevent actual delete
      return { data: params.previousData } as any;
    },
  },
  {
    resource: "contact_notes",
    beforeDelete: async (params, dataProvider) => {
      // Soft delete: set deleted = true instead of removing the row
      await dataProvider.update("contact_notes", {
        id: params.id,
        data: { deleted: true },
        previousData: params.previousData,
      });
      // Return the previousData as the result to prevent actual delete
      return { data: params.previousData } as any;
    },
  },
  {
    resource: "company_notes",
    beforeDelete: async (params, dataProvider) => {
      // Soft delete: set deleted = true instead of removing the row
      await dataProvider.update("company_notes", {
        id: params.id,
        data: { deleted: true },
        previousData: params.previousData,
      });
      // Return the previousData as the result to prevent actual delete
      return { data: params.previousData } as any;
    },
  },
  {
    resource: "deal_notes",
    beforeDelete: async (params, dataProvider) => {
      // Soft delete: set deleted = true instead of removing the row
      await dataProvider.update("deal_notes", {
        id: params.id,
        data: { deleted: true },
        previousData: params.previousData,
      });
      // Return the previousData as the result to prevent actual delete
      return { data: params.previousData } as any;
    },
  },
  {
    resource: "load_notes",
    beforeDelete: async (params, dataProvider) => {
      // Soft delete: set deleted = true instead of removing the row
      await dataProvider.update("load_notes", {
        id: params.id,
        data: { deleted: true },
        previousData: params.previousData,
      });
      // Return the previousData as the result to prevent actual delete
      return { data: params.previousData } as any;
    },
  },
  {
    resource: "loads_summary",
    beforeGetList: async (params) => {
      const withSearch = applyFullTextSearch(["load_number", "status"])(params);
      return remapDateFilter(withSearch);
    },
  },
];

export const getDataProvider = () => {
  if (import.meta.env.VITE_SUPABASE_URL === undefined) {
    throw new Error("Please set the VITE_SUPABASE_URL environment variable");
  }
  if (import.meta.env.VITE_SB_PUBLISHABLE_KEY === undefined) {
    throw new Error(
      "Please set the VITE_SB_PUBLISHABLE_KEY environment variable",
    );
  }
  return withLifecycleCallbacks(
    getDataProviderWithCustomMethods(),
    lifeCycleCallbacks,
  ) as CrmDataProvider;
};

/** Remap friendly filter key names that can't use @ in React Hook Form sources. */
const remapDateFilter = (params: GetListParams): GetListParams => {
  const { pickup_date_eq, ...rest } = params.filter ?? {};
  if (!pickup_date_eq) return params;
  return {
    ...params,
    filter: { ...rest, "pickup_date@eq": pickup_date_eq },
  };
};

export const applyFullTextSearch =
  (
    columns: string[],
    aliases: Record<string, string> = {
      email: "email_fts",
      phone: "phone_fts",
    },
  ) =>
  (params: GetListParams) => {
    if (!params.filter?.q) {
      return params;
    }
    const { q, ...filter } = params.filter;
    return {
      ...params,
      filter: {
        ...filter,
        "@or": columns.reduce(
          (acc, column) => ({
            ...acc,
            [`${aliases[column] ?? column}@ilike`]: q,
          }),
          {},
        ),
      },
    };
  };

const uploadToBucket = async (fi: RAFile) => {
  if (!fi.src.startsWith("blob:") && !fi.src.startsWith("data:")) {
    // Sign URL check if path exists in the bucket
    if (fi.path) {
      const { error } = await getSupabaseClient()
        .storage.from(ATTACHMENTS_BUCKET)
        .createSignedUrl(fi.path, 60);

      if (!error) {
        return fi;
      }
    }
  }

  const dataContent = fi.src
    ? await fetch(fi.src)
        .then((res) => {
          if (res.status !== 200) {
            return null;
          }
          return res.blob();
        })
        .catch(() => null)
    : fi.rawFile;

  if (dataContent == null) {
    // We weren't able to download the file from its src (e.g. user must be signed in on another website to access it)
    // or the file has no content (not probable)
    // In that case, just return it as is: when trying to download it, users should be redirected to the other website
    // and see they need to be signed in. It will then be their responsibility to upload the file back to the note.
    return fi;
  }

  const file = fi.rawFile;
  const fileParts = file.name.split(".");
  const fileExt = fileParts.length > 1 ? `.${file.name.split(".").pop()}` : "";
  const fileName = `${Math.random()}${fileExt}`;
  const filePath = `${fileName}`;
  const { error: uploadError } = await getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .upload(filePath, dataContent);

  if (uploadError) {
    console.error("uploadError", uploadError);
    throw new Error("Failed to upload attachment");
  }

  fi.path = filePath;
  fi.src = filePath;

  // save MIME type
  const mimeType = file.type;
  fi.type = mimeType;

  return fi;
};
